import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { api } from '@/services/api';

const AuthContext = createContext({});

// Reexportado por compatibilidade: vários módulos importam `api` daqui.
export { api };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const cache = localStorage.getItem('@LilyBook:user');
      return cache ? JSON.parse(cache) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  function salvarUser(u) {
    setUser(u);
    if (u) localStorage.setItem('@LilyBook:user', JSON.stringify(u));
    else localStorage.removeItem('@LilyBook:user');
  }

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      salvarUser(data.usuario);
    } catch {
      salvarUser(null);
    }
  }, []);

  useEffect(() => {
    let ativo = true;
    api
      .get('/auth/me')
      .then(({ data }) => {
        if (ativo) salvarUser(data.usuario);
      })
      .catch(() => {
        if (ativo) salvarUser(null);
      })
      .finally(() => {
        if (ativo) setLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Detecta sessão expirada/revogada durante o uso (não só no carregamento da
  // página): qualquer 401 numa chamada autenticada limpa o usuário na hora,
  // em vez de deixar a UI mostrando "logada" até o próximo reload.
  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          salvarUser(null);
        }
        return Promise.reject(error);
      },
    );
    return () => api.interceptors.response.eject(interceptorId);
  }, []);

  async function registro(nome, email, senha) {
    const { data } = await api.post('/auth/registro', { nome, email, senha });
    return data;
  }

  async function cadastrar({ nome, email, cpf, senha, telefone }) {
    const { data } = await api.post('/auth/signup', {
      name: nome,
      email,
      cpf,
      password: senha,
      phone: telefone || null,
    });
    return data;
  }

  // Aceita e-mail ou CPF. O /auth/signin devolve só id/email/role, então
  // recarregamos /auth/me para ter o usuário completo (nome, etc.).
  async function entrar(emailOuCpf, senha) {
    const { data } = await api.post('/auth/signin', {
      email: emailOuCpf,
      password: senha,
    });
    await refreshUser();
    return data;
  }

  async function esqueciSenha(emailOuCpf) {
    const { data } = await api.post('/auth/forgot-password', {
      emailOrCpf: emailOuCpf,
    });
    return data;
  }

  async function redefinirSenha(token, novaSenha) {
    const { data } = await api.post('/auth/reset-password', {
      token,
      newPassword: novaSenha,
    });
    return data;
  }

  async function login(email, senha) {
    const { data } = await api.post('/auth/login', { email, senha });
    if (data?.usuario) salvarUser(data.usuario);
    return data;
  }

  async function loginComGoogle(codigo) {
    const { data } = await api.post('/auth/google/exchange', { codigo });
    if (data?.usuario) salvarUser(data.usuario);
    return data;
  }

  async function verificarOtp(email, otp) {
    const { data } = await api.post('/auth/verify-otp', { email, otp });
    if (data?.usuario) salvarUser(data.usuario);
    return data;
  }

  async function atualizarPerfil(campos) {
    const { data } = await api.patch('/usuario/perfil', campos);
    salvarUser({ ...user, ...data });
    return data;
  }

  async function logout() {
    try {
      await api.post('/auth/logout');
    } catch {}
    salvarUser(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        eAdmin: user?.role === 'ADMIN',
        loading,
        login,
        loginComGoogle,
        logout,
        registro,
        verificarOtp,
        atualizarPerfil,
        refreshUser,
        cadastrar,
        entrar,
        esqueciSenha,
        redefinirSenha,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
